import React, { useEffect, useState } from 'react';
import { fetchRecordByKey } from "../../services/IndexedDBUtil";
import { DATABASE_NAME, STORE_NAME } from '../../services/constants';

export default function Report2({ Product, Location, workflowId }) {

    const [filteredData, setFilteredData] = useState([]);
    const [bestModel, setBestModel] = useState(null);
    const [actualVsPredictedData, setActualVsPredictedData] = useState([]);
    const [showChart, setShowChart] = useState(false);

    useEffect(() => {
        const init = async () => {
            try {
                // Fetch MAPE model data
                let indexedDBResponse = await fetchRecordByKey(DATABASE_NAME, STORE_NAME, 'WFModelResult');
                if (indexedDBResponse?.success) {
                    let _data = indexedDBResponse.record?.data;
                    let skuData = _data.cities
                        .find(cityData => cityData.city === Location)
                        ?.data.find(skuData => skuData.sku === Product)
                        ?.data;

                    setFilteredData(skuData);
                    setBestModel(skuData?.[0]?.model);
                }

                // Fetch Actual vs Predicted data for chart
                let actualPredictedResponse = await fetchRecordByKey(DATABASE_NAME, STORE_NAME, 'WFActualPredictedData');
                if (actualPredictedResponse?.success) {
                    let _data = actualPredictedResponse.record?.data;
                    let skuData = _data.cities.find(cityData => cityData.city === Location)?.data.find(skuData => skuData.sku === Product)?.data;
                    
                    if (skuData) {
                        setActualVsPredictedData(skuData);
                        setShowChart(true);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setShowChart(false);
            }
        };
        init();
    }, [Product, Location]);

    // Sales manager feature removed

    const modelDisclaimers = {
        ARIMA: (
            <>
                <strong>ARIMA</strong> was selected for its strength in modeling stable, non-seasonal patterns; no external (exogenous) variables were used.
            </>
        ),
        ARIMAX: (
            <>
                <strong>ARIMAX</strong> was selected for its ability to combine historical trends with external factors like promotions and pricing; exogenous variables were required.
            </>
        ),
        SARIMA: (
            <>
                <strong>SARIMA</strong> was selected for its ability to capture recurring seasonal patterns in the data, but trend was not included; no external (exogenous) variables were used.
            </>
        ),
        SARIMAX: (
            <>
                <strong>SARIMAX</strong> was selected for its ability to model seasonal patterns and external drivers such as marketing campaigns, but trend was not included; exogenous variables were required.
            </>
        ),
        TES: (
            <>
                <strong>TES</strong> was selected for effectively modeling trends and seasonality in predictable cycles; no external (exogenous) variables were used.
            </>
        )
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-full mx-auto space-y-6 lg:space-y-8">
                {/* Header Section */}
                <div className="bg-white rounded-2xl shadow-2xl p-6 lg:p-8 border-2 border-[#1F4280] transform hover:scale-[1.02] transition-all duration-300">
                    <h2
                        className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2"
                        style={{ fontFamily: 'Poppins, sans-serif', color: '#0F1116' }}
                    >
                        MAPE Report - Actual vs Prediction Analysis
                    </h2>
                    <p
                        className="text-base sm:text-lg lg:text-xl"
                        style={{ fontFamily: 'Inter, sans-serif', color: '#6B7280', fontStyle: 'italic' }}
                    >
                        Comprehensive performance analysis for {Product} in {Location}
                    </p>
                </div>

                {/* Model Disclaimer - 2nd position, now larger and more prominent */}
                {bestModel && (
                    <div className="bg-[#032B4E] border-2 border-[#1F4280] rounded-2xl p-10 sm:p-14 lg:p-16 shadow-2xl hover:shadow-3xl transform hover:scale-[1.03] transition-all duration-300 flex flex-col items-start justify-center">
                        <div className="flex items-center gap-6 mb-4 w-full">
                            <div className="text-white text-5xl lg:text-6xl">ℹ️</div>
                            <h4 className="text-2xl lg:text-4xl font-extrabold mb-0" style={{fontFamily: 'Poppins, sans-serif', color: '#fff', letterSpacing: '0.5px'}}>
                                Model Selection Rationale
                            </h4>
                        </div>
                        <div className="w-full flex justify-center">
                            <div className="w-full bg-white/90 border-4 border-[#032B4E] rounded-2xl shadow-2xl p-6 lg:p-10 flex items-center justify-center">
                                <p className="text-[#032B4E] font-extrabold leading-relaxed text-2xl lg:text-3xl text-center w-full drop-shadow-lg" style={{fontFamily: 'Inter, sans-serif'}}>
                                    {modelDisclaimers[bestModel] || "No disclaimer available."}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Performance Scorecards - Table Format, now more compact */}
                <div className="w-full">
                    {filteredData && filteredData.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-2xl hover:shadow-3xl p-4 sm:p-6 lg:p-8 border-2 border-[#1F4280] transform hover:scale-[1.01] transition-all duration-300 relative overflow-hidden min-h-[400px] sm:min-h-[450px] flex flex-col justify-center">
                            {/* Background Pattern */}
                            <div className="absolute top-0 right-0 w-32 h-32 lg:w-48 lg:h-48 bg-gradient-to-br from-[#032B4E]/5 to-transparent rounded-full -translate-y-12 translate-x-12"></div>
                            <div className="absolute bottom-0 left-0 w-24 h-24 lg:w-36 lg:h-36 bg-gradient-to-tr from-[#032B4E]/5 to-transparent rounded-full translate-y-8 -translate-x-8"></div>
                            <div className="relative z-10 h-full flex flex-col justify-center">
                                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#032B4E] mb-4 flex items-center space-x-3 drop-shadow-sm" style={{fontFamily: 'Poppins, sans-serif'}}>
                                    <span className="text-3xl lg:text-4xl text-[#b8842f]">📊</span>
                                    <span style={{color: '#0F1116'}}>Selected Model Performance</span>
                                </h3>
                                {/* Selected Model Table */}
                                <div className="overflow-x-auto w-full">
                                    {/* Color logic for MAPE and Model Type */}
                                    {(() => {
                                        const trainMAPE = filteredData[0].train_mape;
                                        const testMAPE = filteredData[0].test_mape;
                                        const isTrainGood = trainMAPE <= 10;
                                        const isTestGood = testMAPE <= 10;
                                        const goodColor = '#1bbf4c'; // green
                                        const badColor = '#800020'; // burgundy
                                        // Model badge color: green if both good, burgundy if either bad, else gold
                                        let modelBadgeColor = (isTrainGood && isTestGood) ? goodColor : (!isTrainGood || !isTestGood) ? badColor : '#b8842f';
                                        let modelBadgeTextColor = '#fff';
                                        let trainTextColor = isTrainGood ? goodColor : badColor;
                                        let testTextColor = isTestGood ? goodColor : badColor;
                                        return (
                                            <table className="min-w-full text-xl lg:text-2xl">
                                                <thead className="bg-[#032B4E] text-white">
                                                    <tr>
                                                        <th className="px-4 py-3 text-left font-extrabold tracking-wide drop-shadow-sm text-2xl lg:text-3xl" style={{fontFamily: 'Poppins, sans-serif'}}>Model Details</th>
                                                        <th className="px-4 py-3 text-center font-extrabold tracking-wide drop-shadow-sm text-2xl lg:text-3xl" style={{fontFamily: 'Poppins, sans-serif'}}>Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    <tr className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 transition-all duration-200 h-16 lg:h-20">
                                                        <td className="px-4 py-4 font-extrabold text-gray-900 text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif'}}>Model Type</td>
                                                        <td className="px-4 py-4 text-center">
                                                            <span className="inline-flex items-center px-7 py-3 rounded-full font-extrabold shadow-lg text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif', background: modelBadgeColor, color: modelBadgeTextColor}}>
                                                                {filteredData[0].model}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr className="hover:bg-gradient-to-r hover:from-yellow-50 hover:to-amber-50 transition-all duration-200 h-16 lg:h-20">
                                                        <td className="px-4 py-4 font-extrabold text-gray-900 text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif'}}>Train MAPE</td>
                                                        <td className="px-4 py-4 text-center">
                                                            <span className="font-extrabold text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif', color: trainTextColor}}>
                                                                {trainMAPE.toFixed(2)}%
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 transition-all duration-200 h-16 lg:h-20">
                                                        <td className="px-4 py-4 font-extrabold text-gray-900 text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif'}}>Test MAPE</td>
                                                        <td className="px-4 py-4 text-center">
                                                            <span className="font-extrabold text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif', color: testTextColor}}>
                                                                {testMAPE.toFixed(2)}%
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    {/* <tr className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50 transition-all duration-200 h-16 lg:h-20">
                                                        <td className="px-4 py-4 font-extrabold text-gray-900 text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif'}}>Parameters</td>
                                                        <td className="px-4 py-4 text-center">
                                                            <span className="text-gray-700 font-extrabold bg-gray-100 px-7 py-3 rounded-xl shadow-lg text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif'}}>
                                                                {filteredData[0].param}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50 transition-all duration-200 h-16 lg:h-20">
                                                        <td className="px-4 py-4 font-extrabold text-gray-900 text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif'}}>Seasonal</td>
                                                        <td className="px-4 py-4 text-center">
                                                            <span className="text-gray-700 font-extrabold bg-gray-100 px-7 py-3 rounded-xl shadow-lg text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif'}}>
                                                                {filteredData[0].seasonal}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                    <tr className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-slate-50 transition-all duration-200 h-16 lg:h-20">
                                                        <td className="px-4 py-4 font-extrabold text-gray-900 text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif'}}>Trend</td>
                                                        <td className="px-4 py-4 text-center">
                                                            {filteredData[0].trend ? (
                                                                <span className="text-gray-700 font-extrabold bg-gray-100 px-7 py-3 rounded-xl shadow-lg text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif'}}>
                                                                    {filteredData[0].trend}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-400 text-2xl lg:text-3xl" style={{fontFamily: 'Inter, sans-serif'}}>-</span>
                                                            )}
                                                        </td>
                                                    </tr> */}
                                                </tbody>
                                            </table>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Model Disclaimer - Removed from bottom since moved to 2nd position */}
                {bestModel && (
                    <div className="hidden">
                    </div>
                )}
            </div>
        </div>
    );
}
